import { DestroyRef, Directive, ElementRef, afterNextRender, inject } from '@angular/core';

/** Folga entre o fim da lista aberta e o fim da página. */
const FOLGA_PX = 16;

type ElementoComLista = HTMLElement & { open: boolean; updateComplete?: Promise<unknown> };

/**
 * Faz a página crescer quando a lista de um `vivest-select` ou `vivest-autocomplete` abre e
 * passaria do fim do conteúdo.
 *
 * O app roda num iframe que o iframe-resizer ajusta à altura do conteúdo; a lista do design system
 * é posicionada por cima (absolute) e não entra nessa altura, então perto do fim da página ela era
 * cortada. Enquanto a lista está aberta, a diretiva dá ao campo uma margem inferior só do tamanho
 * que falta: o conteúdo cresce, o iframe acompanha e a lista fica inteira à vista. Ao fechar, a
 * margem é removida. Campos no meio da página, com espaço de sobra, não mudam.
 */
@Directive({
  selector: 'vivest-select, vivest-autocomplete',
  standalone: true,
})
export class VivestListaVisivelDirective {
  private readonly elemento = inject<ElementRef<ElementoComLista>>(ElementRef).nativeElement;

  constructor() {
    const observador = new MutationObserver(() => void this.ajustar());
    afterNextRender(() => observador.observe(this.elemento, { attributes: true, attributeFilter: ['open'] }));
    inject(DestroyRef).onDestroy(() => observador.disconnect());
  }

  private async ajustar(): Promise<void> {
    this.elemento.style.marginBottom = '';
    if (!this.elemento.open) {
      return;
    }
    await this.elemento.updateComplete;
    const lista = this.elemento.shadowRoot?.querySelector<HTMLElement>('.listbox');
    if (!lista) {
      return;
    }
    // A caixa do app-root (ou do body) não inclui a lista posicionada por cima: é o fim do conteúdo.
    const conteudo = this.elemento.closest('app-root') ?? document.body;
    const falta = lista.getBoundingClientRect().bottom + FOLGA_PX - conteudo.getBoundingClientRect().bottom;
    if (falta > 0) {
      this.elemento.style.marginBottom = `${Math.ceil(falta)}px`;
    }
  }
}
